import { useMemo, useState } from "react";
import { artworks, categories, type Artwork } from "@/mocks/gallery";

export default function Gallery() {
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState<Artwork | null>(null);
  const [count, setCount] = useState(8);

  const filtered = useMemo(
    () => (active === "all" ? artworks : artworks.filter((a) => a.category === active)),
    [active]
  );

  const visible = filtered.slice(0, count);

  return (
    <section id="gallery" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Gallery Collection</span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">精选画廊图集</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            从原画设定到高清壁纸，每一幅作品都凝聚了创作者的心血。点击卡片可放大查看细节，感受笔触与光影的层次。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {categories.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => {
                setActive(c.key);
                setCount(8);
              }}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                active === c.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {visible.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setSelected(a)}
              className="group cursor-pointer overflow-hidden rounded-xl bg-background-100 text-left"
            >
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img
                  src={a.image}
                  alt={`${a.title} ${a.style}作品`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-black/45 px-2.5 py-1 text-xs text-white backdrop-blur">
                  {a.category}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-semibold text-foreground-950 md:text-lg">{a.title}</h3>
                <p className="mt-1 text-xs text-foreground-500">@{a.artist} · {a.style}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {a.tags.slice(0, 2).map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={() => setCount(active === "all" ? artworks.length : filtered.length)}
            className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-full border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-700 transition hover:border-primary-400 hover:text-primary-600"
          >
            {count < filtered.length ? "加载更多图集" : "已展示全部图集"}
            <i className={`ri-arrow-down-line ml-2 ${count >= filtered.length ? "opacity-50" : ""}`}></i>
          </button>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 md:p-8"
          onClick={() => setSelected(null)}
        >
          <div
            className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-background-50 md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative md:w-3/5">
              <img
                src={selected.image}
                alt={`${selected.title} ${selected.style}作品`}
                className="h-64 w-full object-cover object-top md:h-full"
              />
            </div>
            <div className="flex flex-col justify-center p-6 md:w-2/5 md:p-8">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">{selected.category}</span>
              <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-950">{selected.title}</h3>
              <p className="mt-2 text-sm text-foreground-600">
                @{selected.artist} · {selected.style} · {selected.year}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">{selected.description}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {selected.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    #{t}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="mt-6 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600 cursor-pointer"
              >
                收起预览
              </button>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white cursor-pointer"
              aria-label="关闭"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}